import { useEffect, useState } from 'react';
import { Box, Button, Link, MobileStepper, Slide, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { KeyboardArrowRight, KeyboardArrowLeft } from '@mui/icons-material';

type QuoteEntry = {
  quote: ReactNode;
  author: string;
  position: string;
  company: string;
  linkedinUrl: string;
};

const quotes: QuoteEntry[] = [
  {
    quote: (
      <>
        Albert has demonstrated exceptional performance during his internship, consistently
        exceeding expectations across multiple high-impact projects. His contributions have spanned
        both technical implementation and collaborative leadership, showing remarkable versatility.
      </>
    ),
    author: 'Kanisk Chakraborty',
    position: 'Software Engineer',
    company: 'StackAdapt',
    linkedinUrl: 'https://www.linkedin.com/in/kaniskc',
  },
  {
    quote: (
      <>
        Albert has consistently delivered high-quality projects on impressive timelines, and has
        been very proactive about asking for more complex projects to lead and own. Every engineer
        who has worked with Albert has sung his praises, and he's been such a bright and uplifting
        member of the team.
      </>
    ),
    author: 'Jean Kim',
    position: 'Senior Engineering Manager',
    company: 'Titan',
    linkedinUrl: 'https://www.linkedin.com/in/jeanyoungkim/',
  },
  {
    quote: (
      <>
        Albert did a fantastic job as a co-op, he was well beyond expectations. He even got
        recognized among the leadership team during one of the delivery review meetings where, just
        through his own efforts, he brought down the cycle time by 4 days.
      </>
    ),
    author: 'Rahul Bhatt',
    position: 'Engineering Manager',
    company: 'Top Hat',
    linkedinUrl: 'https://www.linkedin.com/in/rahul-a-bhatt/',
  },
  {
    quote: (
      <>
        Albert has proven to be an outstanding co-op. He showed initiative at every step of the way,
        constantly seeking to learn new technologies that would help both Searchspring and his
        future career. He actively built relationships with many other employees and proved to be an
        extremely effective collaborator.
      </>
    ),
    author: 'Stephen Smart',
    position: 'Engineering Team Lead',
    company: 'Searchspring',
    linkedinUrl: 'https://www.linkedin.com/in/sdsmart/',
  },
  {
    quote: (
      <>
        Albert has been an integral member of the team this term. His work on a number of time
        sensitive and critical tasks have been both instrumental to the success of the team's goals,
        and reflect a level of work closer to that of a full-time, rather than just a co-op.
      </>
    ),
    author: 'Kristopher Griffin',
    position: 'Full Stack Developer',
    company: 'Imagine Communications',
    linkedinUrl: 'https://www.linkedin.com/in/kristopher-griffin-89a62715b/',
  },
];

export default function Testimonial() {
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('left');

  const maxSteps = quotes.length;
  const handleNext = () => {
    setDirection('left');
    setActiveStep(prev => (prev + 1) % maxSteps);
  };
  const handleBack = () => {
    setDirection('right');
    setActiveStep(prev => (prev - 1 + maxSteps) % maxSteps);
  };

  const quoteData = quotes[activeStep];

  return (
    <Box
      component='section'
      sx={{
        maxWidth: { xs: '80%', sm: 900 },
        mx: 'auto',
        py: 3,
        mb: 6,
        borderTop: 'solid black 1px',
        borderBottom: 'solid black 1px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 300, sm: 250 },
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'space-between',
              gap: 2,
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: 72,
                fontFamily: 'serif',
                lineHeight: 1,
              }}
            >
              “
            </Typography>
            <Slide
              in
              direction={direction}
              mountOnEnter
              unmountOnExit
              key={activeStep} // force re-render for animation
            >
              <Box>
                <Typography
                  variant='body1'
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    mb: 2,
                  }}
                >
                  {quoteData.quote}
                </Typography>
                <Link href={quoteData.linkedinUrl}>
                  <Typography sx={{ textAlign: 'right', fontStyle: 'italic' }}>
                    {quoteData.author}
                  </Typography>
                </Link>
                <Typography sx={{ textAlign: 'right' }}>
                  {quoteData.position}, {quoteData.company}
                </Typography>
              </Box>
            </Slide>
          </Box>
        </Box>
      </Box>

      <MobileStepper
        variant='dots'
        steps={maxSteps}
        position='static'
        activeStep={activeStep}
        nextButton={<Button size='small' onClick={handleNext} startIcon={<KeyboardArrowRight />} />}
        backButton={<Button size='small' onClick={handleBack} startIcon={<KeyboardArrowLeft />} />}
        sx={{ mt: 3, justifyContent: 'center' }}
      />
    </Box>
  );
}
